import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { DocusealForm } from "@docuseal/react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  RegisterReviewSection,
  RegisterReviewSectionTitle,
} from "../../../Account/Register/Register.style";
import { COLORS } from "../../../../shared/theme/colorConfig";
import {
  useDeclineAuthorizationToAssistMutation,
  useInitiateAuthorizationToAssistMutation,
} from "../../../../store/services/privateApi";

export type AssisterRelationship =
  | "property-staff"
  | "caseworker"
  | "family-member"
  | "other";
export type AssistanceReason =
  | "difficulty-writing"
  | "difficulty-understanding"
  | "lep"
  | "other";

export interface AuthorizationToAssistData {
  needsAssistance: boolean;
  signed: boolean;
  assister?: {
    name: string;
    email: string;
    relationship: AssisterRelationship;
    relationshipOther?: string;
    reason: AssistanceReason;
    reasonOther?: string;
  };
  externalSubmissionId?: string;
  applicationFormId?: string;
  signedAt?: string;
}

interface AuthorizationToAssistProps {
  applicationId: string;
  defaultValues?: AuthorizationToAssistData;
  onSubmit: (data: AuthorizationToAssistData) => void | Promise<void>;
  isSaving?: boolean;
}

export const AuthorizationToAssist: React.FC<AuthorizationToAssistProps> = ({
  applicationId,
  defaultValues,
  onSubmit,
  isSaving = false,
}) => {
  const { t } = useTranslation();

  // Lock only after signing per audit-trail requirement. A prior decline is
  // editable — the resident can change their mind to "Yes, I need help".
  const locked = !!defaultValues?.signed;

  const [needsAssistance, setNeedsAssistance] = useState<"yes" | "no" | "">(
    defaultValues?.needsAssistance === true
      ? "yes"
      : defaultValues?.needsAssistance === false
      ? "no"
      : ""
  );
  const [assisterName, setAssisterName] = useState(
    defaultValues?.assister?.name ?? ""
  );
  const [assisterEmail, setAssisterEmail] = useState(
    defaultValues?.assister?.email ?? ""
  );
  const [relationship, setRelationship] = useState<AssisterRelationship | "">(
    defaultValues?.assister?.relationship ?? ""
  );
  const [relationshipOther, setRelationshipOther] = useState(
    defaultValues?.assister?.relationshipOther ?? ""
  );
  const [reason, setReason] = useState<AssistanceReason | "">(
    defaultValues?.assister?.reason ?? ""
  );
  const [reasonOther, setReasonOther] = useState(
    defaultValues?.assister?.reasonOther ?? ""
  );

  const [embedSrc, setEmbedSrc] = useState<string | undefined>(undefined);
  const [submissionId, setSubmissionId] = useState<string | undefined>(
    defaultValues?.externalSubmissionId
  );
  const [formId, setFormId] = useState<string | undefined>(
    defaultValues?.applicationFormId
  );
  const [error, setError] = useState<string | null>(null);

  const [initiate, { isLoading: isInitiating }] =
    useInitiateAuthorizationToAssistMutation();
  const [decline, { isLoading: isDeclining }] =
    useDeclineAuthorizationToAssistMutation();

  const isFormValid =
    !!assisterName.trim() &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(assisterEmail.trim()) &&
    !!relationship &&
    (relationship !== "other" || !!relationshipOther.trim()) &&
    !!reason &&
    (reason !== "other" || !!reasonOther.trim());

  const handleNo = async () => {
    setError(null);
    try {
      await decline({ applicationId }).unwrap();
      await onSubmit({ needsAssistance: false, signed: false });
    } catch (e: any) {
      setError(
        e?.data?.message ??
          t("affordable.application.authorizationToAssist.errorGeneric")
      );
    }
  };

  const handleInitiate = async () => {
    if (!isFormValid) return;
    setError(null);
    try {
      const result = await initiate({
        applicationId,
        assisterName: assisterName.trim(),
        assisterEmail: assisterEmail.trim(),
        relationship: relationship as AssisterRelationship,
        relationshipOther:
          relationship === "other" ? relationshipOther.trim() : undefined,
        reason: reason as AssistanceReason,
        reasonOther: reason === "other" ? reasonOther.trim() : undefined,
      }).unwrap();
      setEmbedSrc(result.residentEmbedSrc);
      setSubmissionId(result.externalSubmissionId);
      setFormId(result.applicationFormId);
    } catch (e: any) {
      setError(
        e?.data?.message ??
          t("affordable.application.authorizationToAssist.errorGeneric")
      );
    }
  };

  const handleSigned = async () => {
    setError(null);
    await onSubmit({
      needsAssistance: true,
      signed: true,
      assister: {
        name: assisterName.trim(),
        email: assisterEmail.trim(),
        relationship: relationship as AssisterRelationship,
        relationshipOther:
          relationship === "other" ? relationshipOther.trim() : undefined,
        reason: reason as AssistanceReason,
        reasonOther: reason === "other" ? reasonOther.trim() : undefined,
      },
      externalSubmissionId: submissionId,
      applicationFormId: formId,
      signedAt: new Date().toISOString(),
    });
  };

  // Locked state — show summary, no editing.
  if (locked) {
    return (
      <RegisterReviewSection>
        <RegisterReviewSectionTitle>
          {t("affordable.application.authorizationToAssist.title")}
        </RegisterReviewSectionTitle>
        <Alert severity="success" sx={{ mb: 2 }}>
          {defaultValues?.signed
            ? t("affordable.application.authorizationToAssist.lockedSigned")
            : t("affordable.application.authorizationToAssist.lockedDeclined")}
        </Alert>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button
            variant="contained"
            onClick={() => onSubmit(defaultValues!)}
            disabled={isSaving}
          >
            {t("affordable.application.continue")}
          </Button>
        </Box>
      </RegisterReviewSection>
    );
  }

  return (
    <RegisterReviewSection>
      {!embedSrc && (
        <>
          <RegisterReviewSectionTitle>
            {t("affordable.application.authorizationToAssist.title")}
          </RegisterReviewSectionTitle>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("affordable.application.authorizationToAssist.description")}
          </Typography>

          <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
            <RadioGroup
              value={needsAssistance}
              onChange={(e) =>
                setNeedsAssistance(e.target.value as "yes" | "no")
              }
            >
              {(["yes", "no"] as const).map((opt) => (
                <Box
                  key={opt}
                  sx={{
                    border: `1px solid ${
                      needsAssistance === opt
                        ? COLORS.darkBlue[500]
                        : COLORS.gray[200]
                    }`,
                    borderRadius: 2,
                    p: 2,
                    mb: 1.5,
                    backgroundColor:
                      needsAssistance === opt ? COLORS.blue[50] : "transparent",
                    transition: "all 0.2s ease",
                  }}
                >
                  <FormControlLabel
                    value={opt}
                    control={<Radio />}
                    sx={{ m: 0, "& .MuiFormControlLabel-label": { flex: 1 } }}
                    label={
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {t(
                          `affordable.application.authorizationToAssist.${opt}`
                        )}
                      </Typography>
                    }
                  />
                </Box>
              ))}
            </RadioGroup>
          </FormControl>
        </>
      )}

      {needsAssistance === "yes" && !embedSrc && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
            {t("affordable.application.authorizationToAssist.assisterDetails")}
          </Typography>

          <TextField
            fullWidth
            label={t(
              "affordable.application.authorizationToAssist.assisterNameLabel"
            )}
            value={assisterName}
            onChange={(e) => setAssisterName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            type="email"
            label={t(
              "affordable.application.authorizationToAssist.assisterEmailLabel"
            )}
            value={assisterEmail}
            onChange={(e) => setAssisterEmail(e.target.value)}
            sx={{ mb: 2 }}
            helperText={t(
              "affordable.application.authorizationToAssist.assisterEmailHint"
            )}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>
              {t(
                "affordable.application.authorizationToAssist.relationshipLabel"
              )}
            </InputLabel>
            <Select
              value={relationship}
              label={t(
                "affordable.application.authorizationToAssist.relationshipLabel"
              )}
              onChange={(e) =>
                setRelationship(e.target.value as AssisterRelationship)
              }
            >
              <MenuItem value="property-staff">
                {t(
                  "affordable.application.authorizationToAssist.relationship.propertyStaff"
                )}
              </MenuItem>
              <MenuItem value="caseworker">
                {t(
                  "affordable.application.authorizationToAssist.relationship.caseworker"
                )}
              </MenuItem>
              <MenuItem value="family-member">
                {t(
                  "affordable.application.authorizationToAssist.relationship.familyMember"
                )}
              </MenuItem>
              <MenuItem value="other">
                {t(
                  "affordable.application.authorizationToAssist.relationship.other"
                )}
              </MenuItem>
            </Select>
          </FormControl>

          {relationship === "other" && (
            <TextField
              fullWidth
              label={t(
                "affordable.application.authorizationToAssist.relationshipOtherLabel"
              )}
              value={relationshipOther}
              onChange={(e) => setRelationshipOther(e.target.value)}
              sx={{ mb: 2 }}
            />
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>
              {t("affordable.application.authorizationToAssist.reasonLabel")}
            </InputLabel>
            <Select
              value={reason}
              label={t(
                "affordable.application.authorizationToAssist.reasonLabel"
              )}
              onChange={(e) => setReason(e.target.value as AssistanceReason)}
            >
              <MenuItem value="difficulty-writing">
                {t(
                  "affordable.application.authorizationToAssist.reason.difficultyWriting"
                )}
              </MenuItem>
              <MenuItem value="difficulty-understanding">
                {t(
                  "affordable.application.authorizationToAssist.reason.difficultyUnderstanding"
                )}
              </MenuItem>
              <MenuItem value="lep">
                {t("affordable.application.authorizationToAssist.reason.lep")}
              </MenuItem>
              <MenuItem value="other">
                {t("affordable.application.authorizationToAssist.reason.other")}
              </MenuItem>
            </Select>
          </FormControl>

          {reason === "other" && (
            <TextField
              fullWidth
              label={t(
                "affordable.application.authorizationToAssist.reasonOtherLabel"
              )}
              value={reasonOther}
              onChange={(e) => setReasonOther(e.target.value)}
              sx={{ mb: 2 }}
            />
          )}

          <FormHelperText sx={{ mb: 2 }}>
            {t("affordable.application.authorizationToAssist.afterSendNote")}
          </FormHelperText>
        </Box>
      )}

      {embedSrc && (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              border: `1px solid ${COLORS.gray[200]}`,
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <DocusealForm src={embedSrc} onComplete={handleSigned} />
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            {t("affordable.application.authorizationToAssist.signWidgetHint")}
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        {needsAssistance === "no" && (
          <Button
            variant="contained"
            onClick={handleNo}
            disabled={isDeclining || isSaving}
          >
            {isDeclining || isSaving ? (
              <CircularProgress size={20} />
            ) : (
              t("affordable.application.continue")
            )}
          </Button>
        )}
        {needsAssistance === "yes" && !embedSrc && (
          <Button
            variant="contained"
            onClick={handleInitiate}
            disabled={!isFormValid || isInitiating}
          >
            {isInitiating ? (
              <CircularProgress size={20} />
            ) : (
              t("affordable.application.authorizationToAssist.sendAndSign")
            )}
          </Button>
        )}
        {/* Embed auto-advances on completion via onComplete; no manual button needed. */}
      </Box>
    </RegisterReviewSection>
  );
};
